<!-- Build panel markup from dvm-layout-builder.component.html, so the form is
     identical to the Layout Builder's. The widget fixes the chart type, so the
     category / type pickers are left out. -->
<ng-template #apiCallConfigBlock let-kind="kind">
  <div class="control-group control-group-full dvm-api-call-config">
    <label class="dvm-api-call-toggle">
      <input type="checkbox" [(ngModel)]="buildApiCallConfig.enabled" />
      <span>{{ kind === 'widget' ? 'Use a custom API call for this chart / table' : 'Use a custom API call for this control' }}</span>
    </label>

    <ng-container *ngIf="buildApiCallConfig.enabled">
      <div class="dvm-api-call-row">
        <label class="dsg-api-method-field">
          <span>Method</span>
        <select class="dvm-api-call-method" aria-label="Custom API method" [(ngModel)]="buildApiCallConfig.method">
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
        </select>
        </label>
        <label class="dsg-api-url-field">
          <span>API URL</span>
        <input type="text" class="dvm-api-call-url" aria-label="Custom API URL" placeholder="https://api.example.com/endpoint" [(ngModel)]="buildApiCallConfig.url" />
        </label>
      </div>

      <label>{{ kind === 'widget' ? 'When this object loads or refreshes' : 'When this control changes' }}</label>
      <!-- ng-select, not a native <select>: a native popup is sized to its
           longest option and spilled past the panel. The panel keeps the
           control's width; the hover tooltip shows a clipped label. -->
      <ng-select
        #apiModeSel
        class="ss-select dvm-api-call-mode"
        [class.is-open]="!!apiModeSel.isOpen()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="apiCallModeOptions(kind)"
        bindLabel="label"
        bindValue="value"
        [clearable]="false"
        [searchable]="false"
        ariaLabel="Custom API mode"
        [(ngModel)]="buildApiCallConfig.mode"
      ></ng-select>

      <ng-container *ngIf="buildApiCallConfig.mode === 'parallel' && kind !== 'widget'">
        <label>Targets this API answers for</label>
        <ng-select
          #apiTargetSel
          class="ss-select"
          [class.is-open]="!!apiTargetSel.isOpen()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="apiCallTargetOptions"
          bindLabel="label"
          bindValue="value"
          [multiple]="true"
          [closeOnSelect]="false"
          [hideSelected]="false"
          [clearable]="true"
          [searchable]="true"
          placeholder="Pick the target(s) your microservice returns"
          [(ngModel)]="buildApiCallConfig.targets"
        ></ng-select>
        <p class="dvm-pt-hint">
          Both requests fire at the same time. The target(s) picked here are answered by your
          API; every other target above still comes from the normal compute API, and the two
          responses are merged. Leave this empty to fire your API as a pure side effect while
          compute keeps serving all targets.
        </p>
        <small class="control-hint" *ngIf="!apiCallTargetOptions.length">
          Add a chart, KPI or input to the dashboard first so it can be assigned here.
        </small>
      </ng-container>

      <ng-container *ngIf="buildApiCallConfig.method !== 'GET'">
        <label>Request body</label>
        <textarea class="dvm-api-call-body" aria-label="Custom API request body" rows="6" [(ngModel)]="buildApiCallConfig.bodyTemplate" spellcheck="false"></textarea>
        <p class="dvm-pt-hint">
          Tokens: <code>{{ '{componentId}' }}</code>
          <ng-container *ngIf="kind === 'widget'; else controlTokens">,
            <code>{{ '{chartType}' }}</code>, <code>{{ '{title}' }}</code>,
            <code>{{ '{datasets}' }}</code>, <code>{{ '{filters}' }}</code>, <code>{{ '{payload}' }}</code> (compute definition)
          </ng-container>
          <ng-template #controlTokens>, <code>{{ '{value}' }}</code> (current value)</ng-template>
          <ng-container *ngIf="kind === 'dropdown'">, <code>{{ '{field}' }}</code></ng-container>
          <ng-container *ngIf="kind === 'upload'">, <code>{{ '{fileName}' }}</code>, <code>{{ '{fileType}' }}</code></ng-container>
          — a token as the WHOLE value of a JSON string (e.g. <code>"{{ kind === 'widget' ? '{payload}' : '{value}' }}"</code>) keeps its native type; embedded in a longer string it's stringified.
        </p>
      </ng-container>

      <p class="dvm-pt-hint">
        Response is expected in the same shape the built-in compute call already returns:
        <code>{{ '{ "results": [{ "componentId": "...", "result": {...} }] }' }}</code>
      </p>
      <p class="dvm-pt-hint" *ngIf="kind === 'widget'">
        Component ID: <code>{{ componentId }}</code>.<br />
        Use this object's componentId in the response. Direct compute responses are also accepted.
        GET sends definition fields as query parameters. In parallel mode the custom result takes priority;
        normal compute supplies this object when your API returns no matching result.
      </p>
    </ng-container>
  </div>
</ng-template>

<div class="dvm-layout-steps-panel dsg-build-panel" [hidden]="!layoutReady || !selected">
  <div class="panel-content steps-panel-content">

      <div class="bridge-chart-settings">
        <div class="bridge-settings-heading"><mat-icon>tune</mat-icon><span>{{ layoutSectionTitle('config', 'Chart configuration') }}</span></div>

        <div class="dsg-build-type"><mat-icon>widgets</mat-icon><span>{{ buildPanelTitle }}</span>
          <button *ngIf="isWaterfallLayout" type="button" class="add-step-button" mat-stroked-button (click)="addWaterfallStep()"><mat-icon>add</mat-icon> Add step</button>
        </div>
        <!-- Title moved into its own 'Title' accordion (Bar & Waterfall layouts).
             For the remaining layouts (kpi/drill-down/…) keep the inline Title
             field here; config-driven — a layout's config section listing
             `title` in its fields shows it, omitting it hides it. -->
        <!-- Bar / Line & Area / Drill-Down surface their Title via a 'Title' format
             accordion, so exclude the inline field for them. -->
        <div class="control-group" *ngIf="!isBarLayout && !isLineAreaLayout && !(isTableMatrixLayout && isTmDrilldown) && fieldEnabled('config', 'title')">
          <label for="bridgeTitle">Title</label>
          <input id="bridgeTitle" type="text" [(ngModel)]="title" placeholder="e.g. Gross Revenue to Net Profit" />
        </div>
        <!-- <div class="selected-data-summary">
          <mat-icon>database</mat-icon>
          <span>{{ selectedDatasets.length ? selectedDatasets.length + ' table(s) selected' : 'Drag a field from the Data pane to select a table' }}</span>
        </div> -->
      </div>

      <ng-container *ngIf="isChartTargetLayout"
        [ngTemplateOutlet]="apiCallConfigBlock" [ngTemplateOutletContext]="{ kind: 'widget' }"></ng-container>

      <app-dvm-relation-hops
        *ngIf="activeRelationHops as relationConfig"
        [config]="relationConfig"
        [value]="relationHops"
        (valueChange)="relationHops = $event"
      ></app-dvm-relation-hops>

      <!-- Waterfall (bridge steps) build form → components/waterfall-step -->
      <app-waterfall-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-waterfall-step>

      <!-- Table & Matrix build form → components/table-matrix-step -->
      <app-table-matrix-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-table-matrix-step>

      <!-- Nested table: levels + per-level columns. -->
      <app-nested-table-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-nested-table-step>

    <!-- Drill-down build form → components/drill-down-step -->
    <app-drill-down-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-drill-down-step>

    <!-- Shared reference-list template: one card per typed reference, scoped to
         a specific WATERFALL bar (`step` set) or a single formula (`step`
         null) — used by the Input-control target formulas below AND by a
         chart's OWN formula field (KPI / horizontal bar), keyed by that
         chart's own componentId rather than a target id. Declared here, above
         every section that uses it, so it's in scope for all of them. -->
    <!-- Dataset picker + status — one definition, used by every chart section. -->
    <ng-template #datasetPicker>
      <div class="control-group" *ngIf="fieldEnabled('config', 'dataset')">
        <label>{{ fieldLabel('config', 'dataset', 'Dataset') }}</label>
        <ng-select
          #ssSharedDataset
          class="ss-select"
          [class.is-open]="!!ssSharedDataset.isOpen()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="entityOptions"
          [loading]="entitiesLoading"
          [clearable]="true"
          [searchable]="true"
          [multiple]="selectedLayoutType === 'NESTED_TABLE'"
          [closeOnSelect]="selectedLayoutType !== 'NESTED_TABLE'"
          [placeholder]="selectedLayoutType === 'NESTED_TABLE' ? 'Select one or more datasets' : 'Pick the data table to render'"
          [ngModel]="selectedLayoutType === 'NESTED_TABLE' ? selectedDatasets : (drillDataset || null)"
          (ngModelChange)="onSelectedDatasetsChange(selectedLayoutType === 'NESTED_TABLE' ? ($event || []) : ($event ? [$event] : []))"
        ></ng-select>
        <small class="control-hint" *ngIf="datasetStatus.kind === 'hint'">{{ datasetStatus.text }}</small>
        <small class="control-warning" *ngIf="datasetStatus.kind === 'error'">
          {{ datasetStatus.text }}
          <button type="button" class="target-reference-add" *ngIf="datasetStatus.canRetry" (click)="reloadDatasets()">Retry</button>
        </small>
      </div>
    </ng-template>

    <ng-template #referenceList let-id="id" let-step="step">
      <div class="target-reference-list">
        <div class="target-reference-card" *ngFor="let ref of targetReferences(id, step)">
          <div class="target-reference-card-header">
            <span>Reference</span>
            <button type="button" class="target-reference-remove" (click)="removeTargetReference(id, ref)" title="Remove reference">&times;</button>
          </div>
          <div class="target-reference-grid">
            <div class="control-group">
              <label>Token</label>
              <input type="text" [(ngModel)]="ref.token" placeholder="e.g. DN Price" />
            </div>
            <div class="control-group">
              <label>Input title</label>
              <input type="text" [(ngModel)]="ref.inputTitle" placeholder="e.g. Price Inflation %" />
            </div>
            <div class="control-group">
              <label>Target label</label>
              <input type="text" [(ngModel)]="ref.targetLabel" placeholder="e.g. Product Margins" />
            </div>
            <div class="control-group">
              <label>Row label</label>
              <input type="text" [(ngModel)]="ref.rowLabel" placeholder="e.g. DN Price" />
            </div>
          </div>
          <small class="control-warning" *ngIf="isDuplicateReferenceToken(id, ref, step)">
            Another reference already uses token "{{ ref.token.trim() }}" — only one is sent. Give each a unique token.
          </small>
        </div>
        <button type="button" class="target-reference-add" (click)="addTargetReference(id, step)">+ Add reference</button>
      </div>
    </ng-template>

    <!-- Horizontal bar build form → components/horizontal-bar-step -->
    <app-horizontal-bar-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-horizontal-bar-step>

    <!-- KPI card build form → components/kpi-step -->
    <app-kpi-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion"></app-kpi-step>

    <!-- Bar & column build form → components/bar-column-step -->
    <app-bar-column-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-bar-column-step>

    <!-- Line & area build form → components/line-area-step -->
    <app-line-area-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-line-area-step>

    <!-- Scatter & bubble build form → components/scatter-bubble-step -->
    <app-scatter-bubble-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-scatter-bubble-step>

    <!-- Pie & composition build form → components/pie-composition-step -->
    <app-pie-composition-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-pie-composition-step>

    <!-- Chart format options — SHARED accordion template (rendered for Bar AND
         Waterfall). Config-driven: formatControls filtered to the ACTIVE layout
         type + grouped by `group`; the capabilityRegistry's `sections` list gates
         which accordions appear. Values fold into the tile on Generate. -->
    <ng-template #chartFormatAccordion>
      <div class="bar-options-accordion" *ngIf="barFormatGroups.length">
        <div class="dvm-format-group" *ngFor="let group of barFormatGroups" [class.is-open]="isBarFormatGroupOpen(group.name)">
          <button type="button" class="dvm-format-group-head" (click)="toggleBarFormatGroup(group.name)">
            <span>{{ group.name }}</span>
            <mat-icon>{{ isBarFormatGroupOpen(group.name) ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>

          <div class="dvm-format-group-body" *ngIf="isBarFormatGroupOpen(group.name)">
            <ng-container *ngFor="let control of group.controls">
            <div class="dvm-format-row" [class.dvm-format-row-half]="control.width === 'half'" *ngIf="formatControlVisible(control, barFormat)">
              <label>{{ control.label }}</label>

              <!-- CRUD endpoints: each gets its OWN request-body editor, right
                   beside its label, so every URL can post a different contract. -->
              <button
                type="button"
                class="dvm-pt-open-btn"
                *ngIf="control.requestBodyEditor"
                [class.is-set]="hasPayloadTemplate(barFormat, control.key)"
                [matTooltip]="'Configure the request body posted to this endpoint'"
                (click)="openPayloadTemplateModalForBar(control)"
              >{{ hasPayloadTemplate(barFormat, control.key) ? 'Body ✓' : 'Body' }}</button>

              <input
                *ngIf="control.control === 'text'"
                type="text"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
                [placeholder]="control.placeholder || control.label"
              />

              <input
                *ngIf="control.control === 'number'"
                type="number"
                [min]="control.min ?? null"
                [max]="control.max ?? null"
                [step]="control.step ?? 1"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
              />

              <input
                *ngIf="control.control === 'color'"
                type="color"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
              />

              <label *ngIf="control.control === 'toggle'" class="dvm-format-toggle">
                <input
                  type="checkbox"
                  [ngModel]="barFormat[control.key]"
                  (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
                />
              </label>

              <ng-select
                *ngIf="control.control === 'select'"
                class="ss-select"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="formatControlOptions(control)"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event ?? ''; onBarFormatChange()"
              ></ng-select>

              <ng-select
                *ngIf="control.control === 'column-select'"
                class="ss-select"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="formatControlOptions(control)"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event || ''; onBarFormatChange()"
              ></ng-select>
            </div>
            </ng-container>
          </div>
        </div>
      </div>
    </ng-template>

    <!-- Dropdown build form → components/dropdown-step -->
    <app-dropdown-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-dropdown-step>

    <!-- File upload build form → components/file-upload-step -->
    <app-file-upload-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-file-upload-step>

    <!-- Popup build form → components/popup-step -->
    <app-popup-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-popup-step>

    <!-- Input box / slider build form → components/input-control-step -->
    <app-input-control-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-input-control-step>

    <!-- Chart interactions: besides filterKey/filterListeners, a CHART can name
         the component(s) it interacts with. Persisted as payload.targets (same
         shape the input controls use, minus per-target formulas) — also
         editable later from the tile header's link icon. OPTIONAL, so it lives
         in a collapsed accordion (steps/axes stay always visible — those are
         required); the badge shows how many targets are wired. -->
    <div class="tm-nav-config" *ngIf="isChartTargetLayout">
      <div class="tm-nav-head" (click)="interactionsSectionOpen = !interactionsSectionOpen">
        <mat-icon>{{ interactionsSectionOpen ? 'expand_more' : 'chevron_right' }}</mat-icon>
        <span>{{ layoutSectionTitle('interactions', 'Chart interactions') }}</span>
        <span class="tm-nav-badge" *ngIf="selectedTargetCharts.length">{{ selectedTargetCharts.length }}</span>
      </div>
      <div class="tm-nav-body" *ngIf="interactionsSectionOpen">
        <small class="control-hint">
          {{ layoutSectionHint('interactions', 'Select the chart(s)/input(s) this chart interacts with — its interactions are sent to these targets.') }}
        </small>
        <div class="control-group control-group-full">
          <label>{{ fieldLabel('interactions', 'targetCharts', 'Target chart(s) / input(s)') }}</label>
          <ng-select
            #ssChartTargets
            class="ss-select ss-select--multi ss-select--full"
            [class.is-open]="!!ssChartTargets.isOpen()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="targetCharts"
            bindLabel="label"
            bindValue="value"
            [multiple]="true"
            [closeOnSelect]="false"
            [hideSelected]="false"
            [clearable]="true"
            [searchable]="true"
            placeholder="Select target chart(s)/input(s)"
            [(ngModel)]="selectedTargetCharts"
          ></ng-select>
          <small class="control-hint" *ngIf="!targetCharts.length">Add more charts or inputs to the dashboard first — they'll appear here as targets.</small>
        </div>
      </div>
    </div>

    <!-- Drill-Down: its format accordions (Formatting / Columns / Drill-Down)
         render HERE — below the Hierarchy dimensions / Measures / Chart-
         interactions block — so the required hierarchy mapping stays at the top
         and the optional formatting sits after the interactions accordion. -->
    <ng-container *ngIf="isTableMatrixLayout && isTmDrilldown" [ngTemplateOutlet]="chartFormatAccordion"></ng-container>

    <div class="wf-generate-row">
      <!-- The span carries the tooltip: a disabled <button> swallows pointer
           events, so a title on the button itself would never show. -->
      <span class="wf-generate-button-wrap" [title]="generateBlockerText">
        <button type="button" class="primary-action" [disabled]="!canGenerate" (click)="generateConfiguration()">
          {{ isGenerating ? (editingTileId ? 'Updating...' : 'Generating...') : (editingTileId ? 'Update Configuration' : 'Generate Configuration') }}
        </button>
      </span>
      <!-- Say WHAT is missing — the greyed-out button alone gives the user no
           way to work out which required field is still empty. -->
      <div class="message wf-generate-missing" *ngIf="!canGenerate && !isGenerating && generateBlockers.length">
        <strong>Still needed:</strong>
        <ul>
          <li *ngFor="let item of generateBlockers">{{ item }}</li>
        </ul>
      </div>
      <div class="message warning-message" *ngIf="generateError">{{ generateError }}</div>
    </div>
  </div>
</div>
<p class="dsg-build-loading" *ngIf="!layoutReady">Loading build form…</p>

<app-payload-template-modal
  *ngIf="payloadTemplateModal"
  [request]="payloadTemplateModal"
  (save)="onPayloadTemplateSaved($event)"
  (cancel)="closePayloadTemplateModal()"
></app-payload-template-modal>
